import { IconCoffee, IconSalad, IconShoppingCart } from '@tabler/icons-react';
import type { Icon as TablerIcon } from '@tabler/icons-react';
import { FeatureFlag, FeatureFlags, FlagEnabledAt } from '../hooks/useFeatureFlags';

/**
 * A one-time modal announcing a major feature, shown on the dashboard the
 * first time an eligible user lands there after release.
 *
 * Adding an announcement = adding an entry here. Seen state persists per user
 * under `seenAnnouncements[id]` (open-keyed server-side, so no backend change
 * is needed), and `requiredFlag` ties an announcement to a flag rollout: the
 * modal appears for each user only once their flag turns on.
 */
export type FeatureAnnouncement = {
  /** Stable kebab-case id, persisted in the user's seenAnnouncements */
  id: string;
  /**
   * Only show while this flag is enabled for the user. Also drives the
   * new-account cutoff: accounts created after the flag's GLOBAL
   * enablement (server-stamped) never see the announcement, because the
   * feature was their baseline from signup. Accounts created before it,
   * including per-user flagged testers during a staged rollout, always
   * can: the feature was unavailable to them regardless of signup date
   * until their flag turned on.
   */
  requiredFlag?: FeatureFlag;
  /** Short pill over the hero image, e.g. "New" */
  badge: string;
  title: string;
  description: string;
  highlights: { icon: TablerIcon; title: string; text: string }[];
  /**
   * Hero photography across the top. One image renders full bleed; two or
   * three render as a strip. focusY = object-position Y%, matching the
   * pencil-in image convention (lower = show more of the top).
   */
  heroImages: { src: string; alt: string; focusY?: number }[];
  ctaLabel: string;
  /** Route the CTA navigates to */
  ctaPath: string;
  secondaryLabel: string;
};

export const FEATURE_ANNOUNCEMENTS: FeatureAnnouncement[] = [
  {
    id: 'breakfast-lunch-planning',
    requiredFlag: 'additional_meals',
    badge: 'New',
    title: 'Plan the whole day',
    description:
      'Your meal plans can now cover breakfasts and lunches alongside dinners. One plan, one grocery list, every meal of the day.',
    highlights: [
      {
        icon: IconCoffee,
        title: 'Breakfasts on repeat',
        text: 'A small rotation of easy morning meals. Batch cook them once if that fits your week.',
      },
      {
        icon: IconSalad,
        title: 'Lunches your way',
        text: "Fresh recipes or lunches built from last night's dinner.",
      },
      {
        icon: IconShoppingCart,
        title: 'One list, one shop',
        text: 'Every ingredient lands in the same grocery list and cart.',
      },
    ],
    heroImages: [
      { src: '/images/pencil-in/pancakes.jpg', alt: 'Pancakes', focusY: 28 },
      { src: '/images/pencil-in/sandwiches.jpg', alt: 'Sandwiches', focusY: 30 },
      // Not the tacos preset: the dinner presets are the older warm
      // editorial batch and clash beside the bright breakfast/lunch shots
      { src: '/images/pencil-in/announcement-dinner.jpg', alt: 'Tacos', focusY: 55 },
    ],
    ctaLabel: 'Plan your week',
    ctaPath: '/planning',
    secondaryLabel: 'Maybe later',
  },
];

/**
 * The first announcement this user should see right now: eligible by flag,
 * account created before the flag's global enablement (no stamp = flag has
 * never gone global = no cutoff; a missing createdAt is treated as an
 * existing account), never seen server-side, and not dismissed this session
 * (the optimistic set bridges the gap until the profile refetch carries
 * seenAnnouncements).
 */
export const pickFeatureAnnouncement = (
  flags: Partial<FeatureFlags> | undefined,
  flagEnabledAt: FlagEnabledAt | undefined,
  seenAnnouncements: Record<string, boolean> | undefined,
  locallyDismissed: Set<string>,
  userCreatedAt: string | undefined
): FeatureAnnouncement | undefined =>
  FEATURE_ANNOUNCEMENTS.find((announcement) => {
    if (announcement.requiredFlag && !flags?.[announcement.requiredFlag]) {
      return false;
    }
    const globallyEnabledAt = announcement.requiredFlag
      ? flagEnabledAt?.[announcement.requiredFlag]
      : undefined;
    if (
      globallyEnabledAt &&
      userCreatedAt &&
      new Date(userCreatedAt).getTime() > new Date(globallyEnabledAt).getTime()
    ) {
      return false;
    }
    return (
      !seenAnnouncements?.[announcement.id] &&
      !locallyDismissed.has(announcement.id)
    );
  });
